/**
 * Variant siblings (PDP)
 *
 * Behavior:
 * - Canonical PDP is the "master" handle for a group
 * - When arriving with ?base=...&sibling=handle, we keep URL as master and AJAX-swap product content
 * - Color picker lists all sibling products and swaps between them without navigating
 * - When swapping, attempts to keep other options (e.g. size) matched by option name + value
 */

(() => {
  // Prevent duplicate initialization: this script may be reinjected during product-info HTML swaps.
  if (window.__VS_PDP_INIT__) return;
  window.__VS_PDP_INIT__ = true;

  const normalize = (s) => (s || '').replace(/\s+/g, ' ').trim();
  const normLower = (s) => normalize(s).toLowerCase();
  const CONNECTOR_WORDS = new Set([
    'edizione',
    'limitata',
    'charm',
    'in',
    'limited',
    'edition',
    // materials/model words that should never start the "color" suffix
    'pelle',
    'liscia',
    'liscio',
    // model/material words that should never be treated as "color"
    'suede',
    'monogrammata',
    'monogrammato',
    'monogramm',
  ]);

  const QS = new URLSearchParams(window.location.search);

  const suggestCache = new Map();
  const STATE = (window.__VS_PDP_STATE__ = window.__VS_PDP_STATE__ || {
    base: null,
    siblings: null,
    masterHandle: null,
    activeHandle: null,
    pendingSelections: null,
  });

  // More reliable than /search/suggest.json: use a theme endpoint backed by storefront search.
  // Also fetch multiple pages to avoid missing valid siblings in relevance-ranked results.
  async function fetchSearchProducts(query) {
    const q = normalize(query);
    if (!q) return [];
    if (suggestCache.has(q)) return await suggestCache.get(q);

    const p = (async () => {
      const toItems = (raw) =>
        (raw || [])
          .map((r) => ({
            title: normalize(r.title),
            titleLower: normLower(r.title),
            url: r.url,
            image: r.image || r.featured_image || null,
            handle:
              r.handle ||
              (r.url ? r.url.split('?')[0].split('/').filter(Boolean).pop() : null),
          }))
          .filter((x) => x.handle && x.url && x.title);

      const fetchPage = async (page) => {
        const url =
          `/search?view=vs-siblings` +
          `&type=product` +
          `&options[prefix]=last` +
          `&options[unavailable_products]=show` +
          `&page=${page}` +
          `&q=${encodeURIComponent(q)}`;
        const res = await fetch(url, { credentials: 'same-origin' });
        if (!res.ok) return { pages: 1, items: [] };
        const txt = await res.text();
        let data = null;
        try {
          data = JSON.parse(txt);
        } catch {
          return { pages: 1, items: [] };
        }
        return { pages: Number(data?.pages || 1) || 1, items: toItems(data?.products) };
      };

      const first = await fetchPage(1);
      const map = new Map(first.items.map((it) => [it.handle, it]));

      const totalPages = Math.max(1, first.pages);
      const cap = Math.min(totalPages, 20);
      for (let page = 2; page <= cap; page++) {
        const next = await fetchPage(page);
        next.items.forEach((it) => map.set(it.handle, it));
      }

      return Array.from(map.values());
    })();

    suggestCache.set(q, p);
    return await p;
  }

  function siblingsForBase(products, base) {
    const prefix = normLower(base) + ' ';
    if (prefix.trim().length < 2) return [];
    return products.filter((p) => {
      if (!p.titleLower.startsWith(prefix) || p.titleLower.length <= prefix.length) return false;
      // Ensure the base is valid for *each* sibling too (prevents mixing groups like "Bella in" vs "Bella in Pelle").
      return isValidBaseForTitle(p.title, base);
    });
  }

  function pickMasterHandle(siblings) {
    return siblings
      .map((s) => s.handle)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b, 'en'))[0];
  }

  function candidatesFromTitle(title) {
    const words = normalize(title).split(' ').filter(Boolean);
    if (words.length < 2) return [];
    const out = [];
    for (let i = 1; i <= words.length - 1; i++) out.push(words.slice(0, i).join(' '));
    return out;
  }

  function suffixInfoFor(title, baseWordCount) {
    const words = normalize(title).split(' ').filter(Boolean);
    const suffixWords = words.slice(baseWordCount);
    const first = (suffixWords[0] || '').toLowerCase();
    return { suffixWords, suffixLen: suffixWords.length, firstSuffixWord: first };
  }

  function isValidBaseForTitle(title, base) {
    const baseWordCount = normalize(base).split(' ').filter(Boolean).length;
    const { suffixLen, firstSuffixWord } = suffixInfoFor(title, baseWordCount);
    if (suffixLen < 1 || suffixLen > 3) return false;
    if (!firstSuffixWord) return false;
    if (CONNECTOR_WORDS.has(firstSuffixWord)) return false;
    return true;
  }

  async function inferGroupFromTitle(fullTitle) {
    const candidates = candidatesFromTitle(fullTitle);
    if (!candidates.length) return null;

    // IMPORTANT (merchant requirement):
    // - Fixed part must match *exactly* (same words, same order)
    // - Color is always at the end
    //
    // Selection:
    // 1) siblings count desc (ensure the group is stable regardless of which sibling you clicked)
    // 2) max base word count (use the longest fixed part only as a tie-breaker)
    // 3) max suffix words asc (allow multi-word colors, but avoid swallowing model descriptors)
    let best = null;
    for (const base of candidates) {
      if (!isValidBaseForTitle(fullTitle, base)) continue;
      const products = await fetchSearchProducts(base);
      const siblings = siblingsForBase(products, base);
      if (siblings.length < 2) continue;

      const baseWordCount = normalize(base).split(' ').filter(Boolean).length;
      // Compute suffix word count for each sibling (words after baseWordCount)
      const suffixWordCounts = siblings.map((s) => {
        return suffixInfoFor(s.title, baseWordCount).suffixLen;
      });
      const maxSuffixWords = Math.max(...suffixWordCounts);
      // Heuristic safety: suffix should look like a color label, not a long phrase.
      // Allows multi-word colors (e.g. "Dark Grey") but prevents merging different models.
      if (maxSuffixWords > 3) continue;

      const score = {
        count: siblings.length,
        baseWordCount,
        maxSuffixWords,
      };

      if (
        !best ||
        score.count > best.score.count ||
        (score.count === best.score.count && score.baseWordCount > best.score.baseWordCount) ||
        (score.count === best.score.count &&
          score.baseWordCount === best.score.baseWordCount &&
          score.maxSuffixWords < best.score.maxSuffixWords)
      ) {
        best = { base, siblings, score };
      }
    }

    if (!best) return null;
    const master = pickMasterHandle(best.siblings);
    if (!master) return null;
    return { base: best.base, siblings: best.siblings, masterHandle: master };
  }

  function getProductInfo() {
    return document.querySelector("product-info[id^='MainProduct-']");
  }

  function getPathHandle() {
    return decodeURIComponent((window.location.pathname.match(/\/products\/([^\/?#]+)/) || [])[1] || '');
  }

  function getCurrentHandle(productInfo) {
    return (
      productInfo?.dataset?.productHandle ||
      decodeURIComponent((window.location.pathname.match(/\/products\/([^\/?#]+)/) || [])[1] || '')
    );
  }

  function getCurrentTitle(productInfo) {
    return productInfo?.dataset?.productTitle || normalize(document.querySelector('h1')?.textContent);
  }

  function captureSelectedOptions(productInfo) {
    const selections = [];
    const vs = productInfo.querySelector('variant-selects');
    if (!vs) return selections;

    // selects
    vs.querySelectorAll('select[name^="options["]').forEach((sel) => {
      const m = sel.name.match(/^options\[(.*)\]$/);
      if (!m) return;
      selections.push({ name: m[1], value: sel.value });
    });

    // radios (fallback): store group name (input_name contains option name)
    vs.querySelectorAll('fieldset input[type="radio"]:checked').forEach((inp) => {
      const name = inp.name ? inp.name.split('-').slice(0, -1).join('-') : '';
      if (name) selections.push({ name, value: inp.value });
    });

    return selections;
  }

  function applySelectedOptions(productInfo, selections) {
    const vs = productInfo.querySelector('variant-selects');
    if (!vs || !selections?.length) return;

    selections.forEach(({ name, value }) => {
      // Prefer selects by exact option name
      const sel = vs.querySelector(`select[name="options[${CSS.escape(name)}]"]`);
      if (sel) {
        const opt = Array.from(sel.options).find((o) => o.value === value);
        if (opt) {
          sel.value = value;
          sel.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }
      }

      // Radios: match by value
      const radios = Array.from(vs.querySelectorAll('input[type="radio"]'));
      const radio = radios.find((r) => r.value === value);
      if (radio) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  function ensurePickerMount(productInfo) {
    let mount = productInfo.querySelector('[data-auto-color-siblings]');
    if (mount) return mount;

    mount = document.createElement('section');
    mount.className = 'product-siblings auto';
    mount.setAttribute('data-auto-color-siblings', 'true');

    // Place the swatches right above the add-to-cart area (closer, less whitespace).
    const buyForm = productInfo.querySelector('section.product-form');
    if (buyForm?.parentNode) {
      buyForm.parentNode.insertBefore(mount, buyForm);
      return mount;
    }

    // Fallback: after variant picker if present
    const variantPicker = productInfo.querySelector('section.variant-picker');
    if (variantPicker?.parentNode) {
      variantPicker.parentNode.insertBefore(mount, variantPicker.nextSibling);
      return mount;
    }

    // Last resort: inside options container
    const options = productInfo.querySelector('.etheryx-product-options .kontainer') || productInfo;
    options.prepend(mount);
    return mount;
  }

  function simplifyNativeColorOption(productInfo) {
    const root = productInfo?.querySelector('section.variant-picker') || productInfo;
    if (!root) return;

    const optionContainers = Array.from(root.querySelectorAll('variant-selects .product-form__input'));
    optionContainers.forEach((el) => {
      // Determine option name as robustly as possible:
      // - dropdown: select[name="options[Colore]"]
      // - swatch/button: inputs use name like "Colore-1" (option.name + '-' + option.position)
      // - fallback: legend/label text
      let optionName = '';

      const sel = el.querySelector('select[name^="options["]');
      if (sel?.name) {
        const m = sel.name.match(/^options\[(.*)\]$/);
        if (m?.[1]) optionName = normalize(m[1]);
      }

      if (!optionName) {
        const inp = el.querySelector('input[type="radio"][name], input[type="hidden"][name]');
        if (inp?.name) optionName = normalize(inp.name.split('-')[0]);
      }

      if (!optionName) {
        const legend = el.querySelector('legend.form__label');
        const label = el.querySelector('label.form__label');
        const raw = normalize((legend || label)?.textContent);
        optionName = normalize((raw || '').split(':')[0]);
      }

      const lowerName = normLower(optionName);
      if (lowerName !== 'colore' && lowerName !== 'color') return;

      // Keep the "COLORE:" row, but remove any duplicated UI (selected value, swatch, inputs),
      // since we render our own sibling color selector below.
      el.style.display = '';
      el.setAttribute('data-vs-simplified-color-option', 'true');

      // 1) Keep label/legend but remove dynamic selected value next to "COLORE:"
      const legend = el.querySelector('legend.form__label');
      if (legend) {
        const selectedValue = legend.querySelector('[data-selected-value]');
        if (selectedValue) selectedValue.style.display = 'none';
        // Ensure it ends with ":" visually (theme already prints it, but keep safe)
        const baseText = normalize(legend.textContent).split(':')[0];
        // Only adjust if legend doesn't already include ':'
        if (!legend.textContent.includes(':') && baseText) {
          legend.textContent = `${baseText}:`;
        }
      }

      const label = el.querySelector('label.form__label');
      if (label) {
        // Some layouts don't have ":"; add it for consistency with merchant expectation.
        if (!label.textContent.includes(':')) label.textContent = `${normalize(label.textContent)}:`;
      }

      // 2) Hide any native swatch circle shown above the value
      el.querySelectorAll('.dropdown-swatch, [data-selected-value].dropdown-swatch').forEach((n) => {
        n.style.display = 'none';
      });

      // 3) Hide the native color controls (swatch group or select), but keep in DOM for variant logic.
      const swatchGroup = el.querySelector('.swatch-group');
      if (swatchGroup) swatchGroup.style.display = 'none';

      const selectWrap = el.querySelector('.select');
      if (selectWrap) selectWrap.style.display = 'none';
      const selectEl = el.querySelector('select[name^="options["]');
      if (selectEl) selectEl.style.display = 'none';
    });
  }

  function installNativeColorCleaner(productInfo) {
    const vs = productInfo?.querySelector('variant-selects');
    if (!vs) return;
    if (vs.dataset.vsColorCleanerInstalled === 'true') return;
    vs.dataset.vsColorCleanerInstalled = 'true';

    let raf = 0;
    const schedule = () => {
      // Only when siblings mode is active
      if (!STATE.base || !STATE.siblings) return;
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => simplifyNativeColorOption(productInfo));
      // Some theme updates happen async after change handlers; re-apply once more.
      setTimeout(() => simplifyNativeColorOption(productInfo), 50);
    };

    vs.addEventListener('change', schedule, true);

    const obs = new MutationObserver(() => schedule());
    obs.observe(vs, { childList: true, subtree: true });

    // initial
    schedule();
  }

  function ensurePickerStyles() {
    if (document.getElementById('vs-siblings-style')) return;
    const style = document.createElement('style');
    style.id = 'vs-siblings-style';
    style.textContent = `
      [data-auto-color-siblings] { margin: 0; padding: 0; }
      /* Horizontal chips (wrap) */
      [data-auto-color-siblings] .vs-heading {
        font-size: 12px;
        letter-spacing: .08em;
        text-transform: uppercase;
        margin: 0 0 8px 0;
        color: var(--labelTitleColor, var(--colorFaded));
      }
      [data-auto-color-siblings] .vs-grid { display:flex; flex-wrap:wrap; gap: 8px; margin: 0; padding: 0; }
      [data-auto-color-siblings] .vs-chip {
        display:flex;
        align-items:center;
        gap: 8px;
        padding: 6px 10px;
        border: 1px solid rgba(0,0,0,.18);
        border-radius: 999px;
        background: #fff;
        cursor: pointer;
        max-width: 100%;
      }
      [data-auto-color-siblings] .vs-chip:hover { background: rgba(0,0,0,.03); }
      [data-auto-color-siblings] .vs-chip:focus-visible { outline: 2px solid #000; outline-offset: 2px; }
      [data-auto-color-siblings] .vs-chip[aria-current="true"] {
        border-color: rgba(0,0,0,.6);
        box-shadow: 0 0 0 1px rgba(0,0,0,.2) inset;
      }
      [data-auto-color-siblings] .vs-thumb {
        width: 22px; height: 22px;
        border-radius: 7px;
        overflow: hidden;
        border: 1px solid rgba(0,0,0,.15);
        flex: 0 0 auto;
      }
      [data-auto-color-siblings] .vs-thumb img { width:100%; height:100%; object-fit:cover; display:block; }
      [data-auto-color-siblings] .vs-chip-name { font-size: 13px; line-height: 1.05; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    `;
    document.head.appendChild(style);
  }

  function cleanProductUrl(url) {
    return (url || '').split('?')[0];
  }

  function renderPicker(productInfo, base, siblings, activeHandle) {
    const mount = ensurePickerMount(productInfo);
    ensurePickerStyles();
    const baseWordCount = normalize(base).split(' ').filter(Boolean).length;

    const items = siblings
      .map((s) => ({
        ...s,
        color: normalize(s.title).split(' ').filter(Boolean).slice(baseWordCount).join(' ').trim(),
      }))
      .filter((s) => s.color.length > 0)
      .sort((a, b) => a.color.localeCompare(b.color, 'it'));

    const activeItem = items.find((x) => x.handle === activeHandle) || items[0];
    const activeColor = activeItem?.color || '';

    mount.innerHTML = `
      <div class="vs-heading">COLORE</div>
      <div class="vs-grid" role="list" aria-label="Colori disponibili">
        ${items
          .map((it) => {
            const img = (it.image || '').replace(/\"/g, '');
            const isCurrent = it.handle === activeHandle;
            return `
              <button
                type="button"
                class="vs-chip"
                data-sibling-handle="${it.handle}"
                data-product-url="${cleanProductUrl(it.url)}"
                aria-current="${isCurrent ? 'true' : 'false'}"
                aria-label="${it.color}${isCurrent ? ' (selezionato)' : ''}"
              >
                <span class="vs-thumb"><img alt="" loading="lazy" src="${img}"></span>
                <span class="vs-chip-name">${it.color}</span>
              </button>
            `;
          })
          .join('')}
      </div>
    `;
  }

  function setPdpUrlFrozen(productInfo) {
    if (!productInfo) return;
    productInfo.dataset.updateUrl = 'false';
  }

  function swapToProduct(productInfo, productUrl, { keepOptions } = { keepOptions: true }) {
    // ProductInfo instance methods exist in the theme's t-product.js
    if (!productInfo?.renderProductInfo || !productInfo?.handleSwapProduct) return;

    const selections = keepOptions ? captureSelectedOptions(productInfo) : [];
    STATE.pendingSelections = selections;
    setPdpUrlFrozen(productInfo);

    const cleanUrl = cleanProductUrl(productUrl);
    const callback = productInfo.handleSwapProduct(cleanUrl, false);
    const requestUrl = `${cleanUrl}?section_id=${encodeURIComponent(productInfo.dataset.section)}`;

    productInfo.renderProductInfo({
      requestUrl,
      targetId: productInfo.id,
      callback,
    });
  }

  function onProductInfoLoaded() {
    const pi = getProductInfo();
    if (!pi) return;

    // Always freeze URL updates if we’re on a master PDP
    if (STATE.masterHandle && getPathHandle() === STATE.masterHandle) {
      setPdpUrlFrozen(pi);
    }

    // Re-apply selections after swap
    if (STATE.pendingSelections) {
      applySelectedOptions(pi, STATE.pendingSelections);
      STATE.pendingSelections = null;
    }

    // Hide the theme's native "Colore" option so our fixed "COLORE" heading stays unchanged.
    if (STATE.base && STATE.siblings) {
      installNativeColorCleaner(pi);
      simplifyNativeColorOption(pi);
    }

    // Re-render picker (needed because product-info node is replaced on swap)
    if (STATE.base && STATE.siblings && STATE.activeHandle) {
      renderPicker(pi, STATE.base, STATE.siblings, STATE.activeHandle);
    }
  }

  async function bootstrap() {
    const productInfo = getProductInfo();
    if (!productInfo) return;

    const currentHandle = getCurrentHandle(productInfo);
    const pathHandle = getPathHandle();
    const title = getCurrentTitle(productInfo);
    if (!currentHandle || !title) return;

    // Determine base + siblings:
    let base = QS.get('base');
    let siblings = null;
    let masterHandle = null;

    if (base) {
      const products = await fetchSearchProducts(base);
      siblings = siblingsForBase(products, base);
      masterHandle = pickMasterHandle(siblings);
    } else {
      const inferred = await inferGroupFromTitle(title);
      if (!inferred) return; // not a siblings-eligible product
      base = inferred.base;
      siblings = inferred.siblings;
      masterHandle = inferred.masterHandle;
    }

    if (!siblings || siblings.length < 2 || !masterHandle) return;

    const siblingHandle = QS.get('sibling'); // clicked-from-collection handle

    // Persist state
    STATE.base = base;
    STATE.siblings = siblings;
    STATE.masterHandle = masterHandle;
    STATE.activeHandle = siblingHandle || currentHandle;

    // Enforce canonical PDP: URL path must be /products/{masterHandle}
    // IMPORTANT: after an AJAX swap, dataset productHandle changes, but path stays master.
    // We must compare against the path handle to avoid redirect loops.
    if (pathHandle !== masterHandle) {
      const url = new URL(`/products/${masterHandle}`, window.location.origin);
      url.searchParams.set('base', base);
      url.searchParams.set('sibling', siblingHandle || currentHandle);
      window.location.replace(url.toString());
      return;
    }

    // Freeze URL updates on master
    setPdpUrlFrozen(productInfo);

    // Hide the theme's native "Colore" option so our fixed "COLORE" heading stays unchanged.
    installNativeColorCleaner(productInfo);
    simplifyNativeColorOption(productInfo);

    // Render picker (active is sibling if provided, else current/master)
    renderPicker(productInfo, base, siblings, STATE.activeHandle);

    // If we came from a non-master product, swap immediately to that product (no URL change)
    if (siblingHandle && siblingHandle !== masterHandle && currentHandle !== siblingHandle) {
      const sib = siblings.find((s) => s.handle === siblingHandle);
      if (sib?.url) {
        swapToProduct(productInfo, sib.url, { keepOptions: false });
      }
    }
  }

  // Delegate click handler so it survives product-info swaps
  document.addEventListener(
    'click',
    (e) => {
      const btn = e.target.closest('[data-auto-color-siblings] button[data-product-url][data-sibling-handle]');
      if (!btn) return;
      e.preventDefault();

      const url = btn.getAttribute('data-product-url');
      const handle = btn.getAttribute('data-sibling-handle');
      if (!url || !handle) return;

      const pi = getProductInfo();
      if (!pi) return;

      STATE.activeHandle = handle;

      swapToProduct(pi, url, { keepOptions: true });
    },
    true
  );

  // Keep behavior correct after swaps
  document.addEventListener('product-info:loaded', onProductInfoLoaded, true);

  // Run after product scripts are loaded (they're deferred), so DOMContentLoaded is fine.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => bootstrap().catch(() => {}), { once: true });
  } else {
    bootstrap().catch(() => {});
  }
})();

